import { useMemo } from 'react'

import { styled } from 'styled-components'

import { type OrchestratedCommonListingDetails } from '@b-stock/fe-core/bstock-next/queries'
import {
  FormattedMessage,
  Button,
  Modal,
  Link,
} from '@b-stock/fe-core/bstock-react'
import {
  Breakpoints,
  Shadows,
  Typography,
} from '@b-stock/fe-core/design-system'
import { designColors } from '@b-stock/fe-core/theme'
import { DocumentDtoDocTypeEnum } from '@b-stock/listing-api-client'

const ns = 'Listing.confirmationModal'

const ActionContainer = styled(Modal.StyledActionContainer)`
  display: flex;
  justify-content: center;
  border: none;
  ${Shadows.Popovers}
  padding: 1rem;
  background: white;
  gap: 1rem;
  margin-top: auto;
  border-radius: 0 0 0.5rem 0.5rem;
  @media ${Breakpoints.max.small} {
    display: grid;
  }
`

const StyledBaseButton = styled(Button)`
  @media ${Breakpoints.max.small} {
    margin-left: 0;
  }
`

const StyledButton = styled(StyledBaseButton)`
  white-space: nowrap;
  order: 2;
  @media ${Breakpoints.max.small} {
    width: 100%;
    order: 1;
  }
`

const StyledCancelButton = styled(StyledBaseButton)`
  white-space: nowrap;
  order: 1;
  @media ${Breakpoints.max.small} {
    width: 100%;
    order: 2;
  }
`

const Checklist = styled.div`
  display: flex;
  width: 19.6rem;
  flex-direction: column;
  position: relative;
  ${Typography.Body3}
  color: ${designColors.neutral.darkGray};
`

const DetailsValue = styled.div`
  position: relative;
  ${Typography.Body3}
  color: ${designColors.neutral.darkGray};

  @media ${Breakpoints.max.small} {
    font-size: 0.7rem;
  }
`

export const ActionSection = ({
  closeModal,
  isLoading,
  listing,
  onConfirm,
  confirmButtonLabel,
}: {
  closeModal: () => void
  isLoading: boolean
  listing: OrchestratedCommonListingDetails
  onConfirm: () => void
  confirmButtonLabel: React.ReactElement
}) => {
  const TermsOfPurchaseLink = useMemo(() => {
    const terms = listing.documents?.find(({ docType }) => {
      return docType === DocumentDtoDocTypeEnum.TermsOfService
    })

    const TermsOfPurchaseLink = (chunks: React.ReactNode) => (
      <Link target="_blank" href={terms?.url ?? '#'}>
        {chunks}
      </Link>
    )

    return TermsOfPurchaseLink
  }, [listing.documents])

  return (
    <ActionContainer>
      <Checklist>
        <DetailsValue data-testid="terms-of-purchase-statement">
          <FormattedMessage
            id={`${ns}.check.agree`}
            values={{
              link: TermsOfPurchaseLink,
              confirmButtonLabel,
            }}
          />
        </DetailsValue>
      </Checklist>
      <StyledCancelButton
        size="medium"
        appearance="tertiary"
        disabled={isLoading}
        onClick={closeModal}
      >
        <FormattedMessage id="Common.cancel" />
      </StyledCancelButton>
      <StyledButton
        size="medium"
        disabled={isLoading}
        onClick={onConfirm}
        appearance={isLoading ? 'loading' : 'primary'}
        type="submit"
      >
        {confirmButtonLabel}
      </StyledButton>
    </ActionContainer>
  )
}
